import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { customTableStyles } from "../../assets/CustomTableStyles";
import DataTable from "react-data-table-component";
import { post, get } from "../../services/api";
import { toast } from "react-toastify";

function EnrollmentsModal({
  onClose,
  enrollments: initialEnrollments,
  onCreditNotesSuccess,
}) {
  const token = localStorage.getItem("token");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const [centerId, setCenterId] = useState(savedCenterId || null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [enrollments, setEnrollments] = useState(initialEnrollments);
  const [selectedRows, setSelectedRows] = useState([]);
  const [triggerRerender, setTriggerRerender] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch enrollments from API
  const fetchEnrollments = async () => {
    try {
      const response = await get(
        `CampusActivity/GetCampusActivityByCenterAndSlotId?CenterId=${centerId}&SlotId=${initialEnrollments[0]?.slotId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
          },
        }
      );
      if (response) {
        setEnrollments(response);
      } else {
        toast.error(response.message || "Failed to fetch enrollments.");
      }
    } catch (error) {
      console.error("Error fetching enrollments:", error);
      toast.error("Error fetching enrollments. Please try again.");
    }
  };

  // Disable row selection if both isPaid and isRefund are true
  const disableRowSelection = (row) => {
    return row.isPaid && row.isRefund;
  };

  const handleSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows);
  };

  // Check for enabling Confirm Paid button
  const isConfirmPaidEnabled = selectedRows.some((row) => !row.isPaid);

  // Check for enabling Credit Notes button
  const isCreditNotesEnabled = selectedRows.some(
    (row) => row.isPaid && !row.isRefund
  );

  const payActivityInvoice = async () => {
    const formData = selectedRows
      .filter((row) => !row.isPaid)
      .map((row) => ({
        id: row.id,
        updatedBy: localStorage.getItem("userId"),
      }));

    if (formData.length === 0) {
      toast.error("No unpaid enrollments selected.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await post(
        "CampusActivity/UpdateInvoiceAmount",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
        // Refresh enrollments data
        await fetchEnrollments();
        setSelectedRows([]);
        setTriggerRerender(!triggerRerender);
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error("Error processing payment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreditNotes = async () => {
    const formData = selectedRows
      .filter((row) => row.isPaid && !row.isRefund)
      .map((row) => ({
        id: row.id,
        updatedBy: localStorage.getItem("userId"),
      }));

    if (formData.length === 0) {
      toast.error("No eligible enrollments selected for credit notes.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await post(
        "CampusActivity/AddUpdateActivityApplicationCreditNotes",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Credit notes created successfully.");
        // Refresh enrollments data
        await fetchEnrollments();
        setSelectedRows([]);
        setTriggerRerender(!triggerRerender);
        // Notify parent component
        if (onCreditNotesSuccess) {
          onCreditNotesSuccess();
        }
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error creating credit notes:", error);
      toast.error("Error creating credit notes. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Student",
      selector: (row) => row.studentName,
      sortable: true,
      width: "220px",
    },
    {
      name: "Iban No.",
      selector: (row) => row.iBanNo,
      sortable: true,
      width: "200px",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
    },
    {
      name: "Paid",
      selector: (row) => {
        if (row.isPaid && row.isRefund) {
          return <span className="badge bg-info fs-10">Credit Note</span>;
        } else if (row.isPaid) {
          return <span className="badge bg-success fs-10">Yes</span>;
        } else {
          return <span className="badge bg-danger fs-10">No</span>;
        }
      },
      sortable: true,
      width: "100px",
    },
    {
      name: "Refund",
      selector: (row) =>
        row.isRefund ? (
          <span className="badge bg-success fs-10">Yes</span>
        ) : (
          <span className="badge bg-danger fs-10">No</span>
        ),
      sortable: true,
      width: "100px",
    },
  ];

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">Enrollments</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <DataTable
                columns={columns}
                data={enrollments}
                pagination
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                dense
                customStyles={customTableStyles}
                highlightOnHover={true}
                onChangePage={setCurrentPage}
                onChangeRowsPerPage={setRowsPerPage}
                selectableRows
                selectableRowDisabled={disableRowSelection}
                onSelectedRowsChange={handleSelectedRowsChange}
                clearSelectedRows={triggerRerender}
              />
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={payActivityInvoice}
                  disabled={isLoading || !isConfirmPaidEnabled}
                >
                  {isLoading ? "Confirming..." : "Confirm Paid"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={handleCreditNotes}
                  disabled={isLoading || !isCreditNotesEnabled}
                >
                  {isLoading ? "Processing..." : "Credit Notes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default EnrollmentsModal;
